/**
 * Orchestrates PostHog tracking for the public media kit SPA.
 *
 * Measures: kit identity (per vertical), content source, where they look
 * (section attention + scroll), what they look at (Cloudinary images:
 * impression / load metrics / dwell), nav, CTAs, completion, session end.
 *
 * Each new kit (fashion, beauty…) carries its own kit_slug, kit_title,
 * layout, and content values automatically.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  identifyMediaKitGroupPostHog,
  trackMediaKitCompletedPostHog,
  trackMediaKitContentLoadedPostHog,
  trackMediaKitCtaClickPostHog,
  trackMediaKitImageDwellPostHog,
  trackMediaKitNavigationPostHog,
  trackMediaKitPageViewPostHog,
  trackMediaKitScrollDepthPostHog,
  trackMediaKitSectionAttentionPostHog,
  trackMediaKitSectionDwellPostHog,
  trackMediaKitSessionEndPostHog,
  trackMediaKitViewPostHog,
  type MediaKitCtaType,
  type MediaKitNavMethod,
  type MediaKitTrackContext,
} from "@/lib/posthogAnalytics";
import {
  countMediaKitImages,
  reportMediaKitImageImpression,
  setMediaKitAnalyticsRuntime,
} from "./analyticsRuntime";
import { mediaKitPublicPath, type PublicNavItem } from "./layout";
import type { MediaKitContent } from "./types";

function classifyCta(href: string, label: string): MediaKitCtaType {
  const h = href.toLowerCase();
  const l = label.toLowerCase();
  if (h.startsWith("mailto:") || l.includes("brief") || l.includes("rate")) {
    return "email_brief";
  }
  if (h.includes("instagram.com") || l.includes("instagram") || l.startsWith("@")) {
    return "instagram";
  }
  if (h.startsWith("http")) return "external_link";
  return "other";
}

function nearestSectionMeta(
  el: Element,
  navItems: PublicNavItem[],
): { sectionId?: string; sectionType?: string } {
  const sectionEl = el.closest("section[id], .mk-page[id]") as HTMLElement | null;
  const sectionId = sectionEl?.id;
  const nav = navItems.find((n) => n.id === sectionId);
  return { sectionId, sectionType: nav?.type };
}

export function useMediaKitAnalytics(args: {
  content: MediaKitContent;
  loading: boolean;
  error: string | null;
  contentSource: "cms" | "fallback";
  routeSlug: string | null;
  /** From API meta.title — kit-specific display name */
  kitTitle?: string | null;
  isGeneralKit?: boolean;
  navItems: PublicNavItem[];
  activeId: string;
  rootRef: React.RefObject<HTMLDivElement | null>;
}) {
  const {
    content,
    loading,
    error,
    contentSource,
    routeSlug,
    kitTitle,
    isGeneralKit,
    navItems,
    activeId,
    rootRef,
  } = args;

  const sessionStart = useRef(
    typeof performance !== "undefined" ? performance.now() : Date.now(),
  );
  const loadStarted = useRef(sessionStart.current);
  const viewedSections = useRef(new Set<string>());
  const maxSectionIndex = useRef(0);
  const completedFired = useRef(false);
  const viewFired = useRef(false);
  const contentLoadedFired = useRef(false);
  const ctaClicks = useRef(0);
  const sectionEnteredAt = useRef(performance.now());
  const prevActiveId = useRef<string | null>(null);
  const lastNavMethod = useRef<MediaKitNavMethod>("scroll");

  // Attention / images / scroll counters
  const imagesImpressed = useRef(new Set<string>());
  const imagesLoadedOk = useRef(0);
  const imagesFailed = useRef(0);
  const maxScrollPct = useRef(0);
  const scrollMilestones = useRef(new Set<number>());
  const sectionAttention = useRef(
    new Map<
      string,
      { maxRatio: number; visibleMs: number; lastEnter: number | null; type: string; label: string; index: number }
    >(),
  );
  const imageVisible = useRef(
    new Map<
      string,
      {
        enteredAt: number;
        maxRatio: number;
        variant?: string;
        role?: string;
        sectionId?: string;
        sectionType?: string;
      }
    >(),
  );

  const resolvedIsGeneral =
    typeof isGeneralKit === "boolean"
      ? isGeneralKit
      : Boolean(!routeSlug);

  const creatorName =
    `${content.profile?.name ?? ""} ${content.profile?.surname ?? ""}`.trim() ||
    null;

  const trackCtx: MediaKitTrackContext = useMemo(
    () => ({
      kit_slug: content.slug || routeSlug || "general",
      kit_id: content.id || null,
      kit_title:
        kitTitle ||
        (resolvedIsGeneral ? "General" : content.slug || routeSlug || "kit"),
      is_general: resolvedIsGeneral,
      content_version: content.version,
      content_source: contentSource,
      enabled_sections: navItems.map((n) => n.type).join(","),
      total_sections: navItems.length,
      public_path: mediaKitPublicPath(
        content.slug || routeSlug || "general",
        resolvedIsGeneral,
      ),
      variant: "desktop",
      creator_name: creatorName,
      brand_name: content.brand?.name ?? null,
      image_count: countMediaKitImages(content),
    }),
    [
      content,
      contentSource,
      navItems,
      routeSlug,
      kitTitle,
      resolvedIsGeneral,
      creatorName,
    ],
  );

  const trackCtxRef = useRef(trackCtx);
  trackCtxRef.current = trackCtx;

  const navItemsRef = useRef(navItems);
  navItemsRef.current = navItems;

  // Keep runtime context for CmsImage load events
  useEffect(() => {
    setMediaKitAnalyticsRuntime(trackCtx);
    return () => setMediaKitAnalyticsRuntime(null);
  }, [trackCtx]);

  // Reset session state when kit changes
  useEffect(() => {
    viewFired.current = false;
    contentLoadedFired.current = false;
    completedFired.current = false;
    viewedSections.current = new Set();
    maxSectionIndex.current = 0;
    ctaClicks.current = 0;
    imagesImpressed.current = new Set();
    imagesLoadedOk.current = 0;
    imagesFailed.current = 0;
    maxScrollPct.current = 0;
    scrollMilestones.current = new Set();
    sectionAttention.current = new Map();
    imageVisible.current = new Map();
    sessionStart.current = performance.now();
    loadStarted.current = performance.now();
  }, [routeSlug, content.id, content.slug]);

  // Content loaded + view + PostHog group for this kit
  useEffect(() => {
    if (loading || contentLoadedFired.current) return;
    contentLoadedFired.current = true;
    const loadingMs = Math.round(performance.now() - loadStarted.current);

    identifyMediaKitGroupPostHog(trackCtx);

    trackMediaKitContentLoadedPostHog({
      ...trackCtx,
      content_source: contentSource,
      load_error: error,
      loading_ms: loadingMs,
    });

    if (!viewFired.current) {
      viewFired.current = true;
      trackMediaKitViewPostHog("desktop", navItems.length, true, trackCtx);
    }
  }, [loading, error, contentSource, trackCtx, navItems.length]);

  // Section spy views + dwell
  useEffect(() => {
    if (loading || !navItems.length) return;

    const items = navItems;
    const prev = prevActiveId.current;
    const next = activeId;
    const now = performance.now();

    if (prev && prev !== next) {
      const prevItem = items.find((n) => n.id === prev);
      const prevIdx = items.findIndex((n) => n.id === prev);
      if (prevItem) {
        trackMediaKitSectionDwellPostHog({
          ...trackCtxRef.current,
          section_id: prevItem.id,
          section_type: prevItem.type,
          section_label: prevItem.label,
          section_index: prevIdx + 1,
          dwell_ms: now - sectionEnteredAt.current,
        });
      }
    }

    if (prev !== next) {
      const item = items.find((n) => n.id === next);
      const idx = items.findIndex((n) => n.id === next);
      if (item && idx >= 0) {
        viewedSections.current.add(item.id);
        maxSectionIndex.current = Math.max(maxSectionIndex.current, idx + 1);

        trackMediaKitPageViewPostHog(
          idx + 1,
          item.label,
          items.length,
          lastNavMethod.current,
          true,
          "desktop",
          {
            ...trackCtxRef.current,
            section_type: item.type,
            section_id: item.id,
          },
        );

        if (lastNavMethod.current !== "scroll") {
          lastNavMethod.current = "scroll";
        }

        if (
          !completedFired.current &&
          viewedSections.current.size >= items.length &&
          items.length > 0
        ) {
          completedFired.current = true;
          const elapsed = Math.round(
            (performance.now() - sessionStart.current) / 1000,
          );
          trackMediaKitCompletedPostHog(items.length, elapsed, "desktop", {
            ...trackCtxRef.current,
            sections_viewed: Array.from(viewedSections.current).join(","),
          });
        }
      }
      sectionEnteredAt.current = now;
      prevActiveId.current = next;
    }
  }, [activeId, loading, navItems]);

  // IntersectionObserver: section attention + image impression/dwell
  useEffect(() => {
    const root = rootRef.current;
    if (!root || loading) return;

    const mq = window.matchMedia("(max-width: 840px)");
    const scrollRoot = mq.matches ? root : null;

    // ── Sections: continuous attention (max ratio + visible time) ──
    const sectionObs = new IntersectionObserver(
      (entries) => {
        const now = performance.now();
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).id;
          if (!id) continue;
          const nav = navItemsRef.current.find((n) => n.id === id);
          if (!nav) continue;
          const idx = navItemsRef.current.findIndex((n) => n.id === id);
          let st = sectionAttention.current.get(id);
          if (!st) {
            st = {
              maxRatio: 0,
              visibleMs: 0,
              lastEnter: null,
              type: nav.type,
              label: nav.label,
              index: idx + 1,
            };
            sectionAttention.current.set(id, st);
          }
          st.maxRatio = Math.max(st.maxRatio, entry.intersectionRatio);
          if (entry.isIntersecting) {
            if (st.lastEnter == null) st.lastEnter = now;
          } else if (st.lastEnter != null) {
            st.visibleMs += now - st.lastEnter;
            st.lastEnter = null;
          }
        }
      },
      {
        root: scrollRoot,
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );

    navItemsRef.current.forEach((n) => {
      const el = root.querySelector(`#${n.id}`);
      if (el) sectionObs.observe(el);
    });

    // ── Images: impression + dwell ──
    const imageObs = new IntersectionObserver(
      (entries) => {
        const now = performance.now();
        for (const entry of entries) {
          const img = entry.target as HTMLImageElement;
          const publicId = img.dataset.mkPublicId;
          if (!publicId) continue;
          const key = publicId;
          const { sectionId, sectionType } = nearestSectionMeta(
            img,
            navItemsRef.current,
          );
          const variant = img.dataset.mkVariant;
          const role = img.dataset.mkRole;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            if (!imagesImpressed.current.has(key)) {
              imagesImpressed.current.add(key);
              reportMediaKitImageImpression({
                publicId,
                variant,
                role,
                alt: img.dataset.mkAlt || img.alt,
                sectionId,
                sectionType,
                intersection_ratio: entry.intersectionRatio,
                src: img.currentSrc || img.src || undefined,
              });
            }
            const prev = imageVisible.current.get(key);
            if (!prev) {
              imageVisible.current.set(key, {
                enteredAt: now,
                maxRatio: entry.intersectionRatio,
                variant,
                role,
                sectionId,
                sectionType,
              });
            } else {
              prev.maxRatio = Math.max(prev.maxRatio, entry.intersectionRatio);
            }
          } else {
            const prev = imageVisible.current.get(key);
            if (prev) {
              const dwell = now - prev.enteredAt;
              trackMediaKitImageDwellPostHog({
                ...trackCtxRef.current,
                public_id: publicId,
                image_variant: prev.variant,
                image_role: prev.role,
                section_id: prev.sectionId,
                section_type: prev.sectionType,
                dwell_ms: dwell,
                max_intersection_ratio: prev.maxRatio,
              });
              imageVisible.current.delete(key);
            }
          }
        }
      },
      {
        root: scrollRoot,
        threshold: [0, 0.2, 0.5, 0.75, 1],
      },
    );

    const observeImages = () => {
      root.querySelectorAll<HTMLImageElement>("img[data-mk-public-id]").forEach((img) => {
        imageObs.observe(img);
      });
    };
    observeImages();
    // Images mount after content paint — re-scan shortly
    const t1 = window.setTimeout(observeImages, 400);
    const t2 = window.setTimeout(observeImages, 1500);
    const mo = new MutationObserver(() => observeImages());
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      sectionObs.disconnect();
      imageObs.disconnect();
      mo.disconnect();
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [loading, rootRef, navItems, content.slug]);

  // Listen image load outcomes via capture on root (counts for session end)
  useEffect(() => {
    const root = rootRef.current;
    if (!root || loading) return;

    // Count loads from data attributes after events fire in capture phase
    // We can't easily hook into reportMediaKitImageLoad results; poll complete imgs
    const tick = window.setInterval(() => {
      let ok = 0;
      let fail = 0;
      root.querySelectorAll<HTMLImageElement>("img[data-mk-public-id]").forEach((img) => {
        if (!img.complete) return;
        if (img.naturalWidth > 0) ok += 1;
        else fail += 1;
      });
      imagesLoadedOk.current = ok;
      imagesFailed.current = fail;
    }, 2000);

    return () => window.clearInterval(tick);
  }, [loading, rootRef, content.slug]);

  // Scroll depth within media kit
  useEffect(() => {
    const root = rootRef.current;
    if (!root || loading) return;

    const mq = window.matchMedia("(max-width: 840px)");
    const milestones = [25, 50, 75, 100];

    const readPct = () => {
      if (mq.matches) {
        const max = root.scrollHeight - root.clientHeight;
        if (max <= 0) return 100;
        return Math.min(100, Math.round((root.scrollTop / max) * 100));
      }
      const max =
        document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return 100;
      return Math.min(100, Math.round((window.scrollY / max) * 100));
    };

    const onScroll = () => {
      const pct = readPct();
      if (pct > maxScrollPct.current) maxScrollPct.current = pct;
      for (const m of milestones) {
        if (pct >= m && !scrollMilestones.current.has(m)) {
          scrollMilestones.current.add(m);
          trackMediaKitScrollDepthPostHog({
            ...trackCtxRef.current,
            scroll_pct: m,
            active_section_id: prevActiveId.current || undefined,
          });
        }
      }
    };

    const target: HTMLElement | Window = mq.matches ? root : window;
    target.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const onMq = () => {
      target.removeEventListener("scroll", onScroll);
    };
    // rebind on mq change is simplified: just keep both
    window.addEventListener("scroll", onScroll, { passive: true });
    root.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      root.removeEventListener("scroll", onScroll);
      void onMq;
    };
  }, [loading, rootRef, content.slug]);

  // Session end
  useEffect(() => {
    return () => {
      const items = navItemsRef.current;
      const ctx = trackCtxRef.current;
      const last = prevActiveId.current;
      const now = performance.now();

      if (last) {
        const prevItem = items.find((n) => n.id === last);
        const prevIdx = items.findIndex((n) => n.id === last);
        if (prevItem) {
          trackMediaKitSectionDwellPostHog({
            ...ctx,
            section_id: prevItem.id,
            section_type: prevItem.type,
            section_label: prevItem.label,
            section_index: Math.max(1, prevIdx + 1),
            dwell_ms: now - sectionEnteredAt.current,
          });
        }
      }

      // Flush section attention
      let bestSection: string | null = null;
      let bestRatio = 0;
      sectionAttention.current.forEach((st, id) => {
        if (st.lastEnter != null) {
          st.visibleMs += now - st.lastEnter;
          st.lastEnter = null;
        }
        if (st.maxRatio > bestRatio) {
          bestRatio = st.maxRatio;
          bestSection = id;
        }
        trackMediaKitSectionAttentionPostHog({
          ...ctx,
          section_id: id,
          section_type: st.type,
          section_label: st.label,
          section_index: st.index,
          max_intersection_ratio: st.maxRatio,
          visible_ms: st.visibleMs,
        });
      });

      // Flush open image dwells
      imageVisible.current.forEach((st, publicId) => {
        trackMediaKitImageDwellPostHog({
          ...ctx,
          public_id: publicId,
          image_variant: st.variant,
          image_role: st.role,
          section_id: st.sectionId,
          section_type: st.sectionType,
          dwell_ms: now - st.enteredAt,
          max_intersection_ratio: st.maxRatio,
        });
      });
      imageVisible.current.clear();

      const elapsed = Math.round((now - sessionStart.current) / 1000);
      trackMediaKitSessionEndPostHog(
        items.length || 1,
        viewedSections.current.size,
        elapsed,
        maxSectionIndex.current,
        "desktop",
        {
          ...ctx,
          sections_viewed: Array.from(viewedSections.current).join(","),
          last_section_id: last,
          cta_clicks: ctaClicks.current,
          images_impressed: imagesImpressed.current.size,
          images_loaded: imagesLoadedOk.current,
          images_failed: imagesFailed.current,
          max_scroll_pct: maxScrollPct.current,
          most_attended_section: bestSection,
          most_attended_ratio: bestRatio || null,
          image_public_ids_seen: Array.from(imagesImpressed.current)
            .slice(0, 40)
            .join(","),
        },
      );

      setMediaKitAnalyticsRuntime(null);
    };
  }, []);

  // CTA clicks
  useEffect(() => {
    const root = rootRef.current;
    if (!root || loading) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor || !root.contains(anchor)) return;

      const href = anchor.getAttribute("href") || "";
      if (!href || href.startsWith("#")) return;

      const label = (anchor.textContent || "").trim().slice(0, 80);
      const ctaType = classifyCta(href, label);
      ctaClicks.current += 1;

      const sectionEl = anchor.closest(
        "section[id], .mk-page[id]",
      ) as HTMLElement | null;
      const sectionId = sectionEl?.id || prevActiveId.current || null;
      const navItem = navItemsRef.current.find((n) => n.id === sectionId);

      trackMediaKitCtaClickPostHog({
        ...trackCtxRef.current,
        cta_type: ctaType,
        cta_label: label,
        href,
        section_id: sectionId || undefined,
        section_type: navItem?.type,
      });
    };

    root.addEventListener("click", onClick, true);
    return () => root.removeEventListener("click", onClick, true);
  }, [rootRef, loading]);

  const trackNavJump = useCallback(
    (toId: string, method: MediaKitNavMethod = "button") => {
      const items = navItemsRef.current;
      const fromId = prevActiveId.current || items[0]?.id || "";
      const fromIdx = items.findIndex((n) => n.id === fromId);
      const toIdx = items.findIndex((n) => n.id === toId);
      if (toIdx < 0) return;

      lastNavMethod.current = method;

      let direction: "next" | "prev" | "jump" = "jump";
      if (fromIdx >= 0) {
        if (toIdx === fromIdx + 1) direction = "next";
        else if (toIdx === fromIdx - 1) direction = "prev";
      }

      const fromItem = items[fromIdx];
      const toItem = items[toIdx];

      trackMediaKitNavigationPostHog(
        direction,
        method,
        fromIdx >= 0 ? fromIdx + 1 : 0,
        toIdx + 1,
        items.length,
        true,
        "desktop",
        {
          ...trackCtxRef.current,
          from_section_id: fromItem?.id,
          to_section_id: toItem?.id,
          from_section_type: fromItem?.type,
          to_section_type: toItem?.type,
        },
      );
    },
    [],
  );

  return { trackNavJump, trackCtx };
}
